"use client"

import { useRouter } from "next/navigation"
import { useTheme } from "next-themes"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import {
  IconBellRinging,
  IconDotsVertical,
  IconLanguage,
  IconLogout,
  IconMoon,
  IconSun,
  IconUser,
} from "@tabler/icons-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenuCheckboxItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuthStore } from "@/stores/auth-store"
import {
  setAppointmentPopupEnabled,
  useAppointmentPopupEnabled,
} from "@/components/appointment-reminder-scheduler"
import { hasRole, ROLES } from "@/lib/roles"
import { isSupportedLocale, normalizeLocale } from "@/lib/i18n"

function getInitials(name: string): string {
  const parts = name.split(" ").filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const t = useTranslations("nav")
  // Locale-switch toasts reuse the Settings → General copy (same action).
  const tLang = useTranslations("settings.language")
  const router = useRouter()
  const { isMobile } = useSidebar()
  const { logout } = useAuthStore()
  const authUser = useAuthStore((state) => state.user)
  const setLocale = useAuthStore((state) => state.setLocale)
  const { theme, setTheme } = useTheme()
  // Phase 88: the upcoming-appointment popup + chime is a per-user mute
  // (localStorage), reachable from here so every role can set it — the
  // profile page only exists for doctors.
  const remindersOn = authUser?.feature_flags?.appointment_reminders_enabled === true
  const popupEnabled = useAppointmentPopupEnabled(authUser?.id)

  // Per-user interface language (Phase 71) — same gate + write path as the
  // Settings → General row, surfaced here so every role can reach it (the
  // settings page needs `settings.general`, which agents don't have).
  const i18nOn = authUser?.feature_flags?.i18n_enabled ?? false
  const currentLocale = normalizeLocale(authUser?.locale)

  const handleLocaleChange = async (value: string) => {
    // Whitelist gate (T-71-01) — never PATCH an unsupported locale.
    if (!isSupportedLocale(value) || value === currentLocale) return
    try {
      // PATCH /auth/me/locale → cookie → client-state swap; re-renders in place.
      await setLocale(value)
      toast.success(value === "it" ? tLang("updatedIt") : tLang("updatedEn"))
    } catch {
      toast.error(tLang("updateFailed"))
    }
  }

  // "My Profile" self-service (Doctor Profiles §2.4) — doctor role + flag
  // (default-true when feature_flags hasn't hydrated yet). Uses the store's
  // full user, not the display-only `user` prop above.
  const canSeeMyProfile =
    hasRole(authUser?.roles, ROLES.DOCTOR) &&
    (authUser?.feature_flags?.doctor_profiles_enabled ?? true)

  const handleLogout = async () => {
    await logout()
    router.push("/login")
  }

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  const initials = getInitials(user.name)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate text-[12.5px] font-semibold">
                  {user.name}
                </span>
                <span className="text-text-dim truncate text-[11px]">
                  {user.email}
                </span>
              </div>
              <IconDotsVertical className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {canSeeMyProfile && (
                <DropdownMenuItem onClick={() => router.push("/profile")}>
                  <IconUser />
                  {t("myProfile")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={toggleTheme}>
                {theme === "dark" ? <IconSun /> : <IconMoon />}
                {theme === "dark" ? t("lightMode") : t("darkMode")}
              </DropdownMenuItem>
              {remindersOn && authUser?.id != null && (
                <DropdownMenuCheckboxItem
                  checked={popupEnabled}
                  onCheckedChange={(checked) =>
                    setAppointmentPopupEnabled(authUser.id, checked === true)
                  }
                  onSelect={(e) => e.preventDefault()}
                >
                  <IconBellRinging className="text-muted-foreground mr-2 size-4" />
                  {t("appointmentPopups")}
                </DropdownMenuCheckboxItem>
              )}
              {i18nOn && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <IconLanguage className="text-muted-foreground mr-2 size-4" />
                    {t("language")}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup
                      value={currentLocale}
                      onValueChange={(v) => void handleLocaleChange(v)}
                    >
                      <DropdownMenuRadioItem value="en">
                        English
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="it">
                        Italiano
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <IconLogout />
              {t("logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
